import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Search, Sparkles, FileText, Loader2, History, Archive, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, PageState, Empty, Field } from "@/components/app/Common";
import { SimpleSelect, entityOptions, listOptions } from "@/components/app/SimpleSelect";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg, openFile } from "@/lib/api";
import { csv, fmtDate } from "@/lib/ui";
import { useAuth, hasFeature } from "@/context/AuthContext";

const TYPES = ["Articles", "By-Laws", "Annual Returns", "Director Resolutions", "Shareholder Resolutions", "Minute Books", "Tax Filings", "Other"];

const UploadDialog = ({ open, onOpenChange, ents, onSaved }) => {
  const [f, setF] = useState({ title: "", doc_type: "Articles", tags: "", entity_id: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
    try {
      await api.post("/documents", fd);
      toast.success("Stored in vault");
      onSaved();
      onOpenChange(false);
      setFile(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle>Upload corporate record</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="File (PDF, image, Word, text)"><Input type="file" onChange={(e) => { setFile(e.target.files[0]); if (!f.title) setF({ ...f, title: e.target.files[0]?.name.replace(/\.[^.]+$/, "") || "" }); }} data-testid="doc-file-input" /></Field>
          <Field label="Title"><Input required value={f.title} onChange={set("title")} data-testid="doc-title-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><SimpleSelect value={f.doc_type} onChange={set("doc_type")} options={listOptions(TYPES)} testId="doc-type-select" /></Field>
            <Field label="Entity"><SimpleSelect value={f.entity_id} onChange={set("entity_id")} allowNone scoped noneLabel="General" options={entityOptions(ents)} testId="doc-entity-select" /></Field>
          </div>
          <Field label="Tags (comma separated)"><Input value={f.tags} onChange={set("tags")} data-testid="doc-tags-input" /></Field>
          <Button type="submit" className="w-full" disabled={busy} data-testid="doc-upload-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Upload</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DocSheet = ({ doc, onClose, reload }) => {
  const ref = useRef();
  const [tags, setTags] = useState("");
  useEffect(() => {
    setTags(doc?.tags.join(", ") || "");
  }, [doc]);
  const addVersion = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("note", "Updated version");
    try {
      await api.post(`/documents/${doc.id}/versions`, fd);
      toast.success("New version uploaded");
      reload();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveTags = async () => {
    try {
      await api.put(`/documents/${doc.id}`, { title: doc.title, doc_type: doc.doc_type, entity_id: doc.entity_id, tags: csv(tags) });
      toast.success("Tags updated");
      reload();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const archive = async () => {
    if (!window.confirm(`Archive "${doc.title}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document archived");
      reload();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Sheet open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="doc-detail-sheet">
        {doc && (
          <>
            <SheetHeader><SheetTitle>{doc.title}</SheetTitle></SheetHeader>
            <div className="text-sm text-muted-foreground mt-1">{doc.doc_type} · {doc.entity_name || "General"}</div>
            <div className="mt-6">
              <Field label="Tags"><Input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="doc-edit-tags-input" /></Field>
              <Button size="sm" variant="outline" className="mt-2" onClick={saveTags} data-testid="doc-save-tags-btn">Save tags</Button>
            </div>
            <div className="mt-8 flex items-center justify-between"><h4 className="font-semibold flex items-center gap-2"><History className="h-4 w-4" />Version history</h4>
              <input ref={ref} type="file" hidden onChange={(e) => e.target.files[0] && addVersion(e.target.files[0])} data-testid="doc-version-input" />
              <Button size="sm" onClick={() => ref.current.click()} data-testid="doc-add-version-btn"><Upload className="h-3.5 w-3.5 mr-1" />New version</Button>
            </div>
            <ul className="mt-3 space-y-2">
              {[...doc.versions].reverse().map((v) => (
                <li key={v.version} className="rounded-lg border border-border p-3 flex items-center gap-3 text-sm" data-testid={`doc-version-${v.version}`}>
                  <span className="font-mono text-xs rounded bg-muted px-1.5 py-0.5">v{v.version}</span>
                  <div className="flex-1 min-w-0"><div className="truncate">{v.filename}</div><div className="text-xs text-muted-foreground">{fmtDate(v.uploaded_at)} · {v.note}</div></div>
                  {v.file_path && <Button size="icon" variant="ghost" onClick={() => openFile(`/documents/${doc.id}/versions/${v.version}/file`).catch(() => toast.error("Could not open file"))} data-testid={`doc-open-v${v.version}`}><Eye className="h-4 w-4" /></Button>}
                </li>
              ))}
            </ul>
            <Button variant="ghost" className="mt-8 text-red-500" onClick={archive} data-testid="doc-archive-btn"><Archive className="h-4 w-4 mr-1" />Archive document</Button>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function Vault() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const state = useLoad(() => Promise.all([api.get("/documents", { params: { q: q || undefined, doc_type: type || undefined } }), api.get("/entities")]).then(([a, b]) => ({ docs: a.data, ents: b.data })), [q, type]);
  const [term, setTerm] = useState("");
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [up, setUp] = useState(false);
  const [sel, setSel] = useState(null);
  const aiSearch = async () => {
    if (!term) return;
    setAiBusy(true);
    try {
      setAi((await api.post("/documents/ai-search", { query: term })).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiBusy(false);
    }
  };
  const docs = ai ? ai.results : state.data?.docs || [];
  const reload = () => {
    setAi(null);
    state.reload();
  };
  return (
    <div>
      <PageHeader eyebrow="Module 5" title="Corporate records vault" subtitle="Articles, by-laws, resolutions, minute books and tax filings — versioned, tagged and searchable.">
        <Button onClick={() => setUp(true)} data-testid="upload-doc-btn"><Upload className="h-4 w-4 mr-1" />Upload record</Button>
      </PageHeader>
      <div className="surface p-4 mb-5 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search titles, tags and contents… or ask AI: “where is our banking resolution?”" value={term}
            onChange={(e) => { setTerm(e.target.value); setAi(null); }} onKeyDown={(e) => e.key === "Enter" && setQ(term)} data-testid="vault-search-input" />
        </div>
        <Button variant="outline" onClick={() => { setAi(null); setQ(term); }} data-testid="vault-search-btn">Search</Button>
        {hasFeature(user, "ai") && <Button onClick={aiSearch} disabled={aiBusy} data-testid="vault-ai-search-btn">{aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}AI search</Button>}
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {["", ...TYPES].map((t) => (
          <button key={t || "all"} onClick={() => { setType(t); setAi(null); }} data-testid={`vault-type-${(t || "all").toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`px-3 py-1.5 text-xs rounded-full border ${type === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{t || "All"}</button>
        ))}
      </div>
      {ai?.answer && <div className="surface p-4 mb-5 text-sm border-primary/30" data-testid="vault-ai-answer"><Sparkles className="h-4 w-4 inline mr-2 text-primary" />{ai.answer}</div>}
      <PageState state={state}>
        {docs.length === 0 ? <Empty icon={FileText} title="No records found" text="Upload articles, by-laws and resolutions to complete your governance score." testId="vault-empty" /> : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="vault-grid">
            {docs.map((d) => (
              <button key={d.id} onClick={() => setSel(d)} className="surface p-5 text-left lift" data-testid={`doc-card-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <div className="font-medium line-clamp-2">{d.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{d.doc_type} · {d.entity_name || "General"}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">{d.tags.map((t) => <span key={t} className="text-[11px] rounded-full bg-muted px-2 py-0.5">#{t}</span>)}</div>
                <div className="text-xs text-muted-foreground mt-3 font-mono">v{d.versions.length} · updated {fmtDate(d.updated_at)}</div>
              </button>
            ))}
          </div>
        )}
      </PageState>
      <UploadDialog open={up} onOpenChange={setUp} ents={state.data?.ents} onSaved={reload} />
      <DocSheet doc={sel} onClose={() => setSel(null)} reload={reload} />
    </div>
  );
}
